<script lang="ts">
	import { ParaglideJS } from '@inlang/paraglide-sveltekit'
	import { onMount } from 'svelte'
	import { MetaTags } from 'svelte-meta-tags'
	import { toast } from 'svelte-sonner'

	import { afterNavigate, beforeNavigate } from '$app/navigation'
	import { page, navigating } from '$app/stores'
	import GetListedDialog from '$lib/components/dialogs/get-listed-dialog.svelte'
	import MetricDialog from '$lib/components/dialogs/metric-dialog.svelte'
	import LoadingBar from '$lib/components/loading-bar.svelte'
	import { Toaster, TwitterToast } from '$lib/components/ui/sonner'
	import { i18n } from '$lib/i18n'
	import AppFooter from '$lib/layouts/app-footer.svelte'
	import AppHeader from '$lib/layouts/app-header.svelte'
	import * as m from '$lib/paraglide/messages'
	import { navigationStore } from '$lib/stores/navigation'
	import { detectDevice, detectDeviceType, phg } from '$lib/utils/posthog'

	import type { LayoutData } from './$types'

	import '../styles/app.css'

	export let data: LayoutData

	let mobileMenu: Promise<typeof import('$lib/layouts/mobile-menu.svelte')> | null = null

	onMount(() => {
		phg.identify(data.userId, {
			$device_type: detectDeviceType(window.navigator.userAgent),
			$device: detectDevice(window.navigator.userAgent),
		})

		window.addEventListener('vite:preloadError', () => {
			window.location.reload()
		})

		if ((data.isMobile || window.innerWidth <= 640) && !mobileMenu) {
			mobileMenu = import('$lib/layouts/mobile-menu.svelte')
		}

		if (import.meta.env.MODE === 'production') {
			// eslint-disable-next-line no-console
			console.info(
				`%cChainspect\n%cHi! Do you need our data? Contact us by email contact@chainspect.app or in Telegram @chainspect_app and we'll try to help you with the data you need!`,
				`
				font-family: Montserrat, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
				font-size: 32px;
				font-weight: 600;
				line-height: 1.5;
				background: linear-gradient(to right, rgb(2, 125, 253), rgb(27, 230, 171));
				-webkit-background-clip: text;
				-webkit-text-fill-color: transparent;
			`,
				`
				font-family: Montserrat, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
				padding: 10px;
				font-size: 14px;
				line-height: 1.2;
				background: rgb(31, 41, 55);
				color: rgb(255, 255, 255);
			`
			)
		}

		const unsubscribe = navigating.subscribe(nav => {
			if (nav !== null && nav.from?.url.pathname !== nav.to?.url.pathname) {
				navigationStore.set('loading')
			} else {
				navigationStore.set('loaded')
			}
		})

		toast(TwitterToast, {
			duration: Infinity,
		})

		return () => {
			unsubscribe()
		}
	})

	afterNavigate(navigation => {
		phg.capture('$pageview', {
			$device_type: detectDeviceType(window.navigator.userAgent),
			$device: detectDevice(window.navigator.userAgent),
			utm_source: navigation.from?.url.searchParams.get('utm_source') ?? null,
			utm_medium: navigation.from?.url.searchParams.get('utm_medium') ?? null,
			utm_campaign: navigation.from?.url.searchParams.get('utm_campaign') ?? null,
			utm_content: navigation.from?.url.searchParams.get('utm_content') ?? null,
			utm_term: navigation.from?.url.searchParams.get('utm_term') ?? null,
		})
	})

	beforeNavigate(navigation => {
		phg.capture('$pageleave', {
			$prev_pageview_pathname: navigation.from?.url.pathname,
			$device_type: detectDeviceType(window.navigator.userAgent),
			$device: detectDevice(window.navigator.userAgent),
		})
	})
</script>

<ParaglideJS {i18n}>
	<MetaTags
		canonical={$page.data.seoTags?.canonical ?? 'https://chainspect.app/'}
		description={$page.data.seoTags?.description ?? m.seo_base_description()}
		openGraph={{
			url: $page.data.seoTags?.canonical ?? 'https://chainspect.app/',
			title: $page.data.seoTags?.title ?? m.seo_base_title(),
			description: $page.data.seoTags?.description ?? m.seo_base_description(),
			type: $page.data.seoTags?.type ?? 'website',
			images: [
				{
					url: $page.data.seoTags?.image ?? 'https://chainspect.app/og',
					width: 1200,
					height: 630,
				},
			],
			siteName: 'Chainspect',
			article: $page.data.seoTags?.article
				? {
						...$page.data.seoTags.article,
						authors: ['https://x.com/chainspect_app'],
					}
				: {},
		}}
		title={$page.data.seoTags?.title ?? m.seo_base_title()}
		twitter={{
			handle: '@chainspect_app',
			site: '@chainspect_app',
			cardType: 'summary_large_image',
			title: $page.data.seoTags?.title ?? m.seo_base_title(),
			description: $page.data.seoTags?.description ?? m.seo_base_description(),
			image: $page.data.seoTags?.image ?? 'https://chainspect.app/og',
		}}
	/>

	<LoadingBar />

	<AppHeader />

	<slot />

	<AppFooter />

	<GetListedDialog />

	<MetricDialog />

	{#if mobileMenu}
		{#await mobileMenu then { default: MobileMenu }}
			<MobileMenu />
		{/await}
	{/if}

	<Toaster closeButton />
</ParaglideJS>
